import { useState } from 'react';
import { Upload, X, Download, FileIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface FilesValue {
  files: Array<{ name: string; url: string; size: number }>;
}

interface FilesCellProps {
  value: FilesValue | null;
  onChange: (value: FilesValue) => void;
  readOnly?: boolean;
}

export function FilesCell({ value, onChange, readOnly }: FilesCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const files = value?.files || [];

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setUploading(true);
    const newFiles: Array<{ name: string; url: string; size: number }> = [];
    
    for (const file of Array.from(e.target.files)) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
        });
        
        if (response.ok) {
          const data = await response.json();
          newFiles.push({
            name: file.name,
            url: data.url,
            size: file.size,
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }
    
    onChange({ files: [...files, ...newFiles] });
    setUploading(false);
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onChange({ files: newFiles });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <>
      <div
        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
        onClick={() => !readOnly && setIsOpen(true)}
      >
        {files.length === 0 ? (
          <span className="text-gray-400 text-sm">Sem arquivos</span>
        ) : (
          <div className="flex items-center gap-1 flex-wrap">
            {files.slice(0, 2).map((file, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs"
              >
                <FileIcon className="w-3 h-3" />
                <span className="max-w-[80px] truncate">{file.name}</span>
              </div>
            ))}
            {files.length > 2 && (
              <span className="text-xs text-gray-500">+{files.length - 2}</span>
            )}
          </div>
        )}
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Arquivos</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!readOnly && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {uploading ? 'Enviando...' : 'Clique para fazer upload'}
                  </span>
                </label>
              </div>
            )}

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {files.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{file.name}</div>
                      <div className="text-xs text-gray-500">{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(file.url, '_blank')}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(idx)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
